/* eslint-disable string-to-lingui/missing-lingui-transformation */

import {plural} from '@lingui/macro'
import React from 'react'
import {useNavigate} from 'react-router-dom'
import styled, {css} from 'styled-components'

import {Button, Inline, NakedButton, Stack, Text, tokens} from '@pleo-io/telescope'
import {ArrowRight, Export} from '@pleo-io/telescope-icons'

import {reportError} from '@product-web/error/report'
import {Container, containerQuery} from '@product-web/telescope-lab/container-queries/container'
import AddMemberModalWrapper, {
    AddMemberModalScreen,
} from '@product-web-features/people/add-modals/add-member-modal/add-member-modal'

import {MilestoneAccordion} from './milestone-accordion'

import {bff} from '../bff-hooks'
import type {MilestoneName, MilestoneStatus, Task} from '../universal/types'

const MOBILE_BREAKPOINT = 400
const CONTAINER_QUERY_NAME = 'BookkeepingMilestoneAccordionItem'

type View = 'initial' | 'tasks' | 'invite-success'

type BookkeepingMilestoneAccordionItemProps = {
    status: MilestoneStatus
    tasks: Task[]
    milestoneName: MilestoneName
    bookkeeperEmails: string[]
    refreshData: () => Promise<void>
}

export const BookkeepingMilestoneAccordionItem = ({
    status,
    tasks,
    milestoneName,
    bookkeeperEmails,
    refreshData,
}: BookkeepingMilestoneAccordionItemProps) => {
    const navigate = useNavigate()
    const {mutate: startTask} =
        bff.companyOnboarding.getStarted.startCompanyOnboardingTask.useMutation()

    const allTasksComplete = tasks.every((task) => task.status === 'COMPLETE')
    const someTasksComplete = tasks.some((task) => task.status === 'COMPLETE')
    const isBookkeeperInvited = Boolean(bookkeeperEmails.length)

    const getInitialView = React.useCallback((): View => {
        if (allTasksComplete) {
            return 'tasks'
        }

        if (isBookkeeperInvited) {
            return 'invite-success'
        }

        if (someTasksComplete) {
            return 'tasks'
        }

        return 'initial'
    }, [allTasksComplete, someTasksComplete, isBookkeeperInvited])

    const [view, setView] = React.useState<View>(getInitialView)

    React.useEffect(() => {
        setView(getInitialView())
    }, [getInitialView])

    const bookkeepingTasks: React.ComponentProps<typeof MilestoneAccordion.Tasks>['tasks'] = tasks
        .map((task) => {
            switch (task.name) {
                case 'LINK_ACCOUNTING_SYSTEM':
                    return {
                        heading: 'Link to your accounting system',
                        status: task.status,
                        cta: (
                            <Button
                                variant="primary"
                                IconRight={ArrowRight}
                                onClick={() => {
                                    startTask(task.name)
                                    navigate('/onboarding/setup-accounting')
                                }}
                            >
                                Continue
                            </Button>
                        ),
                    }

                case 'SET_UP_TAGS':
                    return {
                        heading: 'Set up tags',
                        status: task.status,
                        cta: (
                            <Button
                                variant="primary"
                                IconRight={ArrowRight}
                                onClick={() => {
                                    startTask(task.name)
                                    navigate('/settings/accounting/tags')
                                }}
                            >
                                Continue
                            </Button>
                        ),
                    }

                default: {
                    reportError('Invalid milestone task name', null, {
                        milestoneName,
                        taskName: task.name,
                    })

                    return null
                }
            }
        })
        .filter(getIsNotNull)

    const startBookkeeperInviteFlow = async () => {
        await AddMemberModalWrapper.show({
            initialScreen: AddMemberModalScreen.EXTERNAL_BOOKKEEPER,
            data: [],
            onAddMember: async () => {
                // We need to refresh the data manually here because the invite mutation is not via BFF yet and so
                // doesn't refresh query data automatically
                await refreshData()

                setView('invite-success')
            },
        })
    }

    const bookkeeperInvitedMessage = plural(bookkeeperEmails.length, {
        one: `You invited ${bookkeeperEmails[0]} to help with this.`,
        other: `You invited ${bookkeeperEmails[0]} and ${plural(bookkeeperEmails.length - 1, {
            one: '# other',
            other: '# others',
        })} to help with this.`,
    })

    return (
        <Container name={CONTAINER_QUERY_NAME}>
            <MilestoneAccordion.Item value={milestoneName} status={status}>
                <MilestoneAccordion.Header
                    headingLevel="h2"
                    heading="Simplify your bookkeeping"
                    Icon={Export}
                    progress={getMilestoneProgress(tasks)}
                />

                <MilestoneAccordion.Content>
                    {view === 'initial' && (
                        <MilestoneAccordion.ContentItem>
                            <Stack space={16} stretch>
                                <Text variant="large-accent" weight="medium">
                                    Who will set this up?
                                </Text>

                                <ContentItemButton onClick={() => setView('tasks')}>
                                    I'll do it myself
                                </ContentItemButton>

                                <ContentItemButton onClick={startBookkeeperInviteFlow}>
                                    My bookkeeper will do it
                                </ContentItemButton>
                            </Stack>
                        </MilestoneAccordion.ContentItem>
                    )}

                    {view === 'invite-success' && (
                        <MilestoneAccordion.ContentItem>
                            <Stack space={16} align="center">
                                <Text variant="large-accent">{bookkeeperInvitedMessage}</Text>

                                <Button
                                    variant="secondary"
                                    onClick={() => setView('tasks')}
                                    IconRight={ArrowRight}
                                >
                                    I'll do it myself
                                </Button>
                            </Stack>
                        </MilestoneAccordion.ContentItem>
                    )}

                    {view === 'tasks' && <MilestoneAccordion.Tasks tasks={bookkeepingTasks} />}

                    {view === 'tasks' && !allTasksComplete && (
                        <>
                            {isBookkeeperInvited ? (
                                <MilestoneAccordion.Footer description={bookkeeperInvitedMessage} />
                            ) : (
                                <MilestoneAccordion.Footer
                                    description="Need help with your setup?"
                                    badgeText="Trial"
                                    cta={
                                        <Button
                                            variant="secondary"
                                            onClick={startBookkeeperInviteFlow}
                                            IconRight={ArrowRight}
                                        >
                                            Invite your bookkeeper
                                        </Button>
                                    }
                                />
                            )}
                        </>
                    )}
                </MilestoneAccordion.Content>
            </MilestoneAccordion.Item>
        </Container>
    )
}

type ContentItemButtonProps = {
    onClick: () => void
    children: React.ReactNode
}

const ContentItemButton = ({children, onClick}: ContentItemButtonProps) => {
    return (
        <StyledContentItemButton onClick={onClick}>
            <Inline justifyContent="space-between" alignItems="center" space={12}>
                <Text variant="large-accent" weight="medium" align="left">
                    {children}
                </Text>

                <ArrowRight size={24} />
            </Inline>
        </StyledContentItemButton>
    )
}

const StyledContentItemButton = styled(NakedButton)`
    padding: ${tokens.spacing16};
    border-radius: ${tokens.arc8};
    border: ${tokens.sizeBorderDefault} solid ${tokens.colorBorderInteractive};
    background-color: inherit;

    &:hover {
        border: ${tokens.sizeBorderDefault} solid ${tokens.colorBorderInteractiveHover};
    }

    ${containerQuery(
        {name: CONTAINER_QUERY_NAME, minWidth: MOBILE_BREAKPOINT},
        css`
            padding: ${tokens.spacing28} ${tokens.spacing24};
        `,
    )}
`

const getMilestoneProgress = <T extends Pick<Task, 'status'>>(tasks: T[]) => ({
    total: tasks.length,
    completed: tasks.filter((task) => task.status === 'COMPLETE').length,
})

const getIsNotNull = <T,>(value: T): value is Exclude<T, null> => value !== null
